import React from 'react'
import { useHistory } from 'react-router-dom'

const ActionItems = ({ projectId, summary, scopeProgress, bySection, vendorQuoteSummary, contextSummary, onOpenContextDrawer }) => {
  const history = useHistory()
  const items = []

  // Context-layer items (blockers, overdue tasks, upcoming reviews)
  if (contextSummary?.counts?.blockersOpen > 0) {
    items.push({
      alert: true,
      text: `${contextSummary.counts.blockersOpen} open blocker${contextSummary.counts.blockersOpen !== 1 ? 's' : ''}`,
      actionLabel: 'View',
      onClick: () => onOpenContextDrawer && onOpenContextDrawer('blockers')
    })
  }

  if (contextSummary?.counts?.tasksOpen > 0) {
    const overdueTasks = (contextSummary.needsAttention || []).filter(i => i.object_type === 'task')
    if (overdueTasks.length > 0) {
      items.push({
        warn: true,
        text: `${overdueTasks.length} overdue task${overdueTasks.length !== 1 ? 's' : ''}`,
        actionLabel: 'View',
        onClick: () => onOpenContextDrawer && onOpenContextDrawer('tasks')
      })
    }
  }

  if (contextSummary?.counts?.reviewsUpcoming > 0) {
    items.push({
      text: `${contextSummary.counts.reviewsUpcoming} upcoming review${contextSummary.counts.reviewsUpcoming !== 1 ? 's' : ''}`,
      actionLabel: 'View',
      onClick: () => onOpenContextDrawer && onOpenContextDrawer('reviews')
    })
  }

  // Assembly scope progress items (from DEV26Q2)
  const assemblyTotal = scopeProgress?.total || 0
  const needsService = scopeProgress?.needsService || 0
  const needsConfirmation = scopeProgress?.needsConfirmation || 0
  const needsEstimating = scopeProgress?.needsEstimating || 0

  if (needsService > 0) {
    items.push({
      alert: true,
      text: `${needsService} Assembly Item${needsService !== 1 ? 's' : ''} need service templates`,
      actionLabel: 'Open Assembly',
      onClick: () => history.push(`/bid-estimation/${projectId}/scope`)
    })
  }

  if (needsConfirmation > 0) {
    items.push({
      warn: true,
      text: `${needsConfirmation} linked Assembly Item${needsConfirmation !== 1 ? 's' : ''} need confirmation`,
      actionLabel: 'Review Assembly',
      onClick: () => history.push(`/bid-estimation/${projectId}/scope`)
    })
  }

  if (needsEstimating > 0) {
    items.push({
      warn: true,
      text: `${needsEstimating} confirmed Assembly Item${needsEstimating !== 1 ? 's' : ''} have not been sent to estimating`,
      actionLabel: 'Send to Estimating',
      onClick: () => history.push(`/bid-estimation/${projectId}/scope`)
    })
  }

  const missingItems = summary?.missingItems || 0
  if (assemblyTotal === 0 && missingItems > 0) {
    items.push({
      alert: true,
      text: `${missingItems} line item${missingItems !== 1 ? 's' : ''} need pricing`,
      actionLabel: 'Open Assembly',
      onClick: () => history.push(`/bid-estimation/${projectId}/scope`)
    })
  }

  const pending = vendorQuoteSummary?.pendingQuotes || []
  for (const q of pending) {
    items.push({
      text: `Review ${q.vendor || 'vendor'} quote${q.lineItemCount ? ` (${q.lineItemCount} items)` : ''}`,
      actionLabel: 'Review',
      onClick: () => history.push(`/bid-estimation/${projectId}/vendor-quotes`)
    })
  }

  const expired = vendorQuoteSummary?.expiredQuotes || []
  for (const q of expired) {
    const expDate = q.expirationDate ? new Date(q.expirationDate).toLocaleDateString() : ''
    items.push({
      alert: true,
      text: `${q.vendor || 'Vendor'} quote expired ${expDate}`,
      actionLabel: 'Review',
      onClick: () => history.push(`/bid-estimation/${projectId}/vendor-quotes`)
    })
  }

  if (assemblyTotal === 0 && bySection) {
    Object.entries(bySection).forEach(([section, data]) => {
      if (data.items > 0 && data.covered < data.items) {
        const pct = Math.round((data.covered / data.items) * 100)
        if (pct < 80) {
          items.push({
            warn: true,
            text: `${section} is ${pct}% covered`,
            actionLabel: 'Review Assembly',
            onClick: () => history.push(`/bid-estimation/${projectId}/scope`)
          })
        }
      }
    })
  }

  if (items.length === 0 && summary?.totalItems > 0) {
    items.push({ ok: true, text: 'All items have pricing' })
  }

  const urgentCount = items.filter(i => i.alert).length

  const dotColor = (item) => {
    if (item.alert) return 'var(--risk)'
    if (item.warn) return 'var(--warn)'
    if (item.ok) return 'var(--ok)'
    return 'var(--info)'
  }

  return (
    <div className="hmi-panel">
      <div className="hmi-panel-head">
        <h4>Needs Your Attention</h4>
        {urgentCount > 0 && <span style={{ color: 'var(--risk)', fontSize: 12, fontWeight: 600 }}>{urgentCount} urgent</span>}
      </div>
      <div className="hmi-panel-body">
        {items.length === 0 ? (
          <div style={{ padding: 14, color: 'var(--ink-3)', fontSize: 13 }}>No data yet.</div>
        ) : (
          items.map((item, idx) => (
            <div
              key={idx}
              className={`hmi-list-item${item.alert ? ' alert' : ''}`}
              style={{ cursor: item.onClick ? 'pointer' : 'default', alignItems: 'flex-start' }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: dotColor(item),
                flexShrink: 0, marginTop: 6
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5 }}>{item.text}</div>
                {item.actionLabel && (
                  <a
                    style={{ fontSize: 12, color: 'var(--brand, var(--primary))', cursor: 'pointer', fontWeight: 600 }}
                    onClick={(e) => { e.stopPropagation(); item.onClick && item.onClick() }}
                  >
                    {item.actionLabel} {'\u203A'}
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default ActionItems
